export interface ClerkAuthCacheIdentity {
  isLoaded: boolean;
  isSignedIn: boolean | undefined;
  userId: string | null | undefined;
  sessionId: string | null | undefined;
}

export type AuthQueryCacheScope = "signed-out" | `signed-in:${string}:${string}`;

/**
 * A cache scope exists only when Clerk's identity is complete. An inconsistent
 * or still-loading auth snapshot must render nothing, never cached account data.
 */
export function authQueryCacheScope(
  identity: ClerkAuthCacheIdentity,
): AuthQueryCacheScope | null {
  if (!identity.isLoaded) return null;
  if (identity.isSignedIn === false) return "signed-out";
  if (
    identity.isSignedIn !== true
    || !identity.userId
    || !identity.sessionId
  ) {
    return null;
  }
  return `signed-in:${identity.userId}:${identity.sessionId}`;
}

export interface QueryCacheClearer {
  clear: () => void;
}

export interface AuthQueryCacheCommit {
  scope: AuthQueryCacheScope | null;
  ready: boolean;
}

/** Clear on first use and every identity/session/sign-out boundary. */
export function commitAuthQueryCacheScope(
  cache: QueryCacheClearer,
  previousScope: AuthQueryCacheScope | null | undefined,
  nextScope: AuthQueryCacheScope | null,
  clearTenantTransientState?: () => void,
): AuthQueryCacheCommit {
  if (previousScope === undefined || previousScope !== nextScope) {
    // Query parameters live outside TanStack Query. Clear them in the same
    // closed interval so user B cannot inherit user A's product filter on the
    // first request after a Clerk user/session/sign-out transition.
    clearTenantTransientState?.();
    cache.clear();
  }
  return { scope: nextScope, ready: nextScope !== null };
}
